'use strict'

/**
 * Convert an NYCT subway platform stop id (for example R14N or 127S) to its three-character
 * parent-station id. This intentionally recognizes only the canonical four-character subway
 * form; bus and third-party ids merely ending in N/S pass through unchanged.
 */
function parentStopId(stopId) {
  return /^(?:[A-Z]\d{2}|\d{3})[NS]$/.test(stopId) ? stopId.slice(0, -1) : stopId
}

/** Remove only the terminal X that marks an express variant of a subway route. */
function baseRouteId(routeId) {
  return routeId.replace(/X$/, '')
}

/**
 * Great-circle distance in meters between two latitude/longitude coordinates.
 * Named fields are intentional: numeric tuples make a latitude/longitude swap too easy.
 */
function haversineMeters(from, to) {
  const earthRadiusMeters = 6_371_000
  const radiansPerDegree = Math.PI / 180
  const fromLat = from.latitude * radiansPerDegree
  const toLat = to.latitude * radiansPerDegree
  const deltaLat = (to.latitude - from.latitude) * radiansPerDegree
  const deltaLon = (to.longitude - from.longitude) * radiansPerDegree
  const haversine =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(fromLat) * Math.cos(toLat) * Math.sin(deltaLon / 2) ** 2
  return 2 * earthRadiusMeters * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine))
}

/** Subway routes in the numbered-then-lettered order riders see on the system map. */
const SUBWAY_ROUTE_ORDER = Object.freeze([
  '1', '2', '3', '4', '5', '6', '6X', '7', '7X',
  'A', 'C', 'E', 'B', 'D', 'F', 'FX', 'M', 'G', 'J', 'Z', 'L',
  'N', 'Q', 'R', 'W', 'S', 'GS', 'FS', 'H', 'SI',
])
const SUBWAY_ROUTE_RANK = new Map(SUBWAY_ROUTE_ORDER.map((id, index) => [id, index]))

/** Subway routes first in system-map order, then other route ids in natural order. */
function compareRoutes(a, b) {
  const aRank = SUBWAY_ROUTE_RANK.get(a)
  const bRank = SUBWAY_ROUTE_RANK.get(b)
  if (aRank !== undefined && bRank !== undefined) return aRank - bRank
  if (aRank !== undefined) return -1
  if (bRank !== undefined) return 1
  return a.localeCompare(b, undefined, { numeric: true })
}

/** Shared alert-category labels and urgency; runtimes map tone to their own palette. */
const ALERT_TYPE_PRESENTATIONS = Object.freeze({
  'Suspended':        { label: 'Suspended',        severity: 0,  tone: 'danger' },
  'Part Suspended':   { label: 'Partly suspended', severity: 1,  tone: 'danger' },
  'Cancellations':    { label: 'Cancellations',    severity: 2,  tone: 'danger' },
  'Delays':           { label: 'Delays',           severity: 3,  tone: 'danger' },
  'Expect Delays':    { label: 'Expect delays',    severity: 4,  tone: 'warning' },
  'Stops Skipped':    { label: 'Stops skipped',    severity: 5,  tone: 'warning' },
  'Reroute':          { label: 'Reroute',          severity: 6,  tone: 'warning' },
  'Detour':           { label: 'Detour',            severity: 7,  tone: 'warning' },
  'Express to Local': { label: 'Running local',    severity: 8,  tone: 'warning' },
  'Reduced Service':  { label: 'Reduced service',  severity: 9,  tone: 'warning' },
  'Boarding Change':  { label: 'Boarding change',  severity: 10, tone: 'info' },
  'Special Schedule': { label: 'Special schedule', severity: 11, tone: 'info' },
  'Station Notice':   { label: 'Station notice',   severity: 12, tone: 'muted' },
  'Special Notice':   { label: 'Notice',           severity: 13, tone: 'muted' },
  'Extra Service':    { label: 'Extra service',    severity: 14, tone: 'info' },
})
const ALERT_FALLBACK_PRESENTATION = Object.freeze({
  label: 'Service notice', severity: 15, tone: 'muted',
})

/** Presentation metadata for an MTA alert_type, retaining unknown category names. */
function alertTypePresentation(alertType) {
  if (!alertType) return ALERT_FALLBACK_PRESENTATION
  return ALERT_TYPE_PRESENTATIONS[alertType] ?? {
    ...ALERT_FALLBACK_PRESENTATION,
    label: alertType,
  }
}

/**
 * NYC Ferry publishes its own GTFS, and its bare ids collide with the MTA's: ferry landings
 * `113`/`118` are also 1-train platform ids, so an unprefixed ferry arrival would land in the
 * subway station's arrival index. Every ferry route and stop id is carried with this prefix
 * everywhere past the feed parser, which also makes "is this a ferry?" a string test.
 */
const FERRY_ID_PREFIX = 'FERRY-'

/** Namespace a raw NYC Ferry GTFS route or stop id (`ER` → `FERRY-ER`). Idempotent. */
function ferryId(rawId) {
  return rawId.startsWith(FERRY_ID_PREFIX) ? rawId : FERRY_ID_PREFIX + rawId
}

/** Whether an id is a namespaced NYC Ferry route or stop id. */
function isFerryId(id) {
  return typeof id === 'string' && id.startsWith(FERRY_ID_PREFIX)
}

/** The feed's own id (`FERRY-ER` → `ER`); anything not namespaced passes through. */
function ferryRawId(id) {
  return isFerryId(id) ? id.slice(FERRY_ID_PREFIX.length) : id
}

/**
 * The two MTA commuter railroads publish their own GTFS, and their bare ids collide with
 * everyone else's: LIRR stop `1` is Albertson and Metro-North stop `1` is Grand Central, and
 * both feeds number their routes 1, 2, 3… — so, like the ferry, every railroad route, stop
 * and trip id carries its agency as a prefix (`LIRR-102` is Jamaica, `MNR-1` Grand Central)
 * everywhere past the feed parser. The prefix also answers "which railroad?" as a string test.
 */
const RAIL_AGENCIES = Object.freeze(['LIRR', 'MNR'])

/** Namespace a raw railroad GTFS id under its agency (`('LIRR', '102')` → `LIRR-102`). Idempotent. */
function railId(agency, rawId) {
  const prefix = `${agency}-`
  return rawId.startsWith(prefix) ? rawId : prefix + rawId
}

/** The agency a namespaced railroad id belongs to, or null for any other id. */
function railAgencyOf(id) {
  if (typeof id !== 'string') return null
  for (const agency of RAIL_AGENCIES) if (id.startsWith(`${agency}-`)) return agency
  return null
}

/** Whether an id is a namespaced LIRR or Metro-North route, stop or trip id. */
function isRailId(id) {
  return railAgencyOf(id) !== null
}

/** The feed's own id (`LIRR-102` → `102`); anything not namespaced passes through. */
function railRawId(id) {
  const agency = railAgencyOf(id)
  return agency ? id.slice(agency.length + 1) : id
}

/**
 * NJ Transit's bus network is a separate agency with its own GTFS, and its bare ids collide
 * with the MTA's at every level: route `1` is Newark–Jersey City, not the 1 train, and its
 * stop ids are plain integers. So every NJ Transit bus route, stop and trip id carries this
 * prefix (`NJB-113`, `NJB-26229`) everywhere past the feed parser, like the ferry's and the
 * railroads'. (NJ Transit RAIL, when it lands, gets a prefix of its own.)
 */
const NJ_BUS_ID_PREFIX = 'NJB-'

/** Namespace a raw NJ Transit bus GTFS id (`113` → `NJB-113`). Idempotent. */
function njBusId(rawId) {
  return rawId.startsWith(NJ_BUS_ID_PREFIX) ? rawId : NJ_BUS_ID_PREFIX + rawId
}

/** Whether an id is a namespaced NJ Transit bus route, stop or trip id. */
function isNjBusId(id) {
  return typeof id === 'string' && id.startsWith(NJ_BUS_ID_PREFIX)
}

/** The feed's own id (`NJB-113` → `113`); anything not namespaced passes through. */
function njBusRawId(id) {
  return isNjBusId(id) ? id.slice(NJ_BUS_ID_PREFIX.length) : id
}

module.exports = {
  ALERT_TYPE_PRESENTATIONS,
  FERRY_ID_PREFIX,
  NJ_BUS_ID_PREFIX,
  RAIL_AGENCIES,
  SUBWAY_ROUTE_ORDER,
  alertTypePresentation,
  baseRouteId,
  compareRoutes,
  ferryId,
  ferryRawId,
  haversineMeters,
  isFerryId,
  isNjBusId,
  isRailId,
  njBusId,
  njBusRawId,
  parentStopId,
  railAgencyOf,
  railId,
  railRawId,
}
